import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi, type User, type LoginCredentials, type RegisterData } from '@/api/auth'
import { connectWebSocket, disconnectWebSocket } from '@/composables/useWebSocket'

export const useAuthStore = defineStore('auth', () => {
  // State
  const accessToken = ref<string | null>(localStorage.getItem('access_token'))
  const refreshToken = ref<string | null>(localStorage.getItem('refresh_token'))
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const source = ref<string>(localStorage.getItem('user_source') || 'soko') // Phase 20.1: User source (soko or bangbet)
  const thirdPartyId = ref<string | null>(localStorage.getItem('third_party_id')) // Phase 20.1: BangBet user ID

  // Getters
  // 有 token 就认为是登录状态，user 数据会在后台获取
  const isAuthenticated = computed(() => !!accessToken.value)
  const isLoggedIn = computed(() => !!accessToken.value)
  const isBangBetUser = computed(() => source.value === 'bangbet') // Phase 20.1: Check if BangBet user

  // Actions
  const setTokens = (token: string, refresh: string, userSource: string = 'soko', thirdParty: string | null = null) => {
    accessToken.value = token
    refreshToken.value = refresh
    source.value = userSource
    thirdPartyId.value = thirdParty || null
    localStorage.setItem('access_token', token)
    localStorage.setItem('refresh_token', refresh)
    localStorage.setItem('user_source', userSource)
    if (thirdParty) {
      localStorage.setItem('third_party_id', thirdParty)
    } else {
      localStorage.removeItem('third_party_id')
    }
  }

  const clearTokens = () => {
    accessToken.value = null
    refreshToken.value = null
    user.value = null
    source.value = 'soko'
    thirdPartyId.value = null
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user_source')
    localStorage.removeItem('third_party_id')
  }

  const setUser = (userData: User | null) => {
    user.value = userData
    // Phase 20.1: Persist country_code so the AppLayout currency formatter
    // can render the correct symbol on first paint without waiting for fetchCurrentUser.
    if (userData?.country_code) {
      localStorage.setItem('user_country_code', userData.country_code)
    } else if (userData === null) {
      localStorage.removeItem('user_country_code')
    }
  }

  // Phase 20.1: cached country code (read on AppLayout first render before user is fetched)
  const countryCode = ref<string | null>(localStorage.getItem('user_country_code') || null)

  const clearError = () => {
    error.value = null
  }

  // 发送验证码
  const sendCode = async (phone: string, type: 'register' | 'login' | 'reset_password') => {
    isLoading.value = true
    error.value = null
    try {
      const response = await authApi.sendCode({ phone, type })
      return response.code === 0
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to send code'
      return false
    } finally {
      isLoading.value = false
    }
  }

  // 注册
  const register = async (data: RegisterData) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await authApi.register(data)
      if (response.code === 0 && response.data) {
        setTokens(response.data.access_token, response.data.refresh_token)
        setUser(response.data.user)
        // Connect WebSocket after successful registration
        connectWebSocket(response.data.access_token)
        return true
      }
      error.value = response.message || 'Registration failed'
      return false
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Registration failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  // 登录
  const login = async (credentials: LoginCredentials) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await authApi.login(credentials)
      if (response.code === 0 && response.data) {
        setTokens(response.data.access_token, response.data.refresh_token)
        setUser(response.data.user)
        // Connect WebSocket after successful login
        connectWebSocket(response.data.access_token)
        return true
      }
      error.value = response.message || 'Login failed'
      return false
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Login failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  // 登出
  const logout = async () => {
    if (refreshToken.value) {
      try {
        await authApi.logout({ refresh_token: refreshToken.value })
      } catch {
        // Ignore error
      }
    }
    // Disconnect WebSocket before clearing tokens
    disconnectWebSocket()
    clearTokens()
  }

  // 重置密码
  const resetPassword = async (phone: string, code: string, newPassword: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await authApi.resetPassword({ phone, code, new_password: newPassword })
      if (response.code === 0) {
        return true
      }
      error.value = response.message || 'Password reset failed'
      return false
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Password reset failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  // 获取当前用户
  const fetchCurrentUser = async () => {
    if (!accessToken.value) return false
    try {
      const response = await authApi.getCurrentUser()
      if (response.code === 0 && response.data) {
        setUser(response.data.user)
        return true
      }
      return false
    } catch (err: any) {
      // 只在 401（未授权）错误时清除 token
      const status = err.response?.status
      if (status === 401) {
        clearTokens()
      }
      return false
    }
  }

  // Phase 20.1: Handle BangBet redirect login
  const handleBangBetRedirect = async () => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    const userSource = params.get('source')

    if (token && userSource === 'bangbet') {
      // Extract user info from JWT token (Phase 20.1)
      try {
        const tokenParts = token.split('.')
        if (tokenParts.length === 3) {
          // Decode JWT payload (second part)
          const payload = JSON.parse(atob(tokenParts[1]))
          setTokens(token, '', 'bangbet', payload.third_party_id || null)

          // Fetch user info
          await fetchCurrentUser()

          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname)
        }
      } catch (err) {
        console.error('Failed to handle BangBet redirect:', err)
      }
    }
  }

  // 初始化 - 检查token有效性
  const init = async () => {
    // Phase 20.1: Check for BangBet redirect
    await handleBangBetRedirect()

    if (accessToken.value) {
      await fetchCurrentUser()
    }
  }

  return {
    // State
    accessToken,
    refreshToken,
    user,
    isLoading,
    error,
    source,
    thirdPartyId,
    countryCode,
    // Getters
    isAuthenticated,
    isLoggedIn,
    isBangBetUser,
    // Actions
    sendCode,
    register,
    login,
    logout,
    resetPassword,
    fetchCurrentUser,
    init,
    clearError,
    setTokens,
    clearTokens,
    handleBangBetRedirect,
  }
})
